/**
 * Map a failed desk write to the copy Dominique reads (D-11), so a raw DB code
 * is never shown and a write that did not happen is never shown as a success
 * (T-33-39).
 *
 * Every desk decision is guarded by `processed = false`. A guarded write that
 * matches 0 rows (another tab or person decided first) comes back from the RPC
 * as false / null; the decision data layer (33-19) turns that into an error
 * whose `name` is `DESK_ALREADY_DECIDED`. That string is the whole contract:
 * there is no import between the two files. `desk_not_restorable` (0035
 * `desk_restore_raw_item`, 22023) means the same thing for a restore.
 *
 * The 0035 RPCs raise `desk_admin_only` with 42501, and RLS / a missing grant
 * also answer 42501: all of them get the "check that you are signed in as an
 * admin" copy. `desk_bad_image` (22023) is the image swap's own failure. Every
 * other error, including a 23514 refusal by a named desk CHECK
 * (`applications_desk_*`), is "That did not save".
 *
 * Pure: no Supabase call and no translation lookup. Keys are in en and pt-PT
 * (33-04).
 */

export const DESK_ALREADY_DECIDED = 'DeskAlreadyDecidedError'

export type DeskErrorKey =
  | 'desk.error.already_decided_title'
  | 'desk.error.already_decided_body'
  | 'desk.error.load'
  | 'desk.error.save'
  | 'desk.images.change_failed'

export type DeskErrorMessage = {
  /** A notification title, or null for a message-only notification. */
  titleKey: DeskErrorKey | null
  messageKey: DeskErrorKey
  /** True when the caller must re-read the candidate (it now shows "Already decided"). */
  alreadyDecided: boolean
}

const ALREADY_DECIDED: DeskErrorMessage = {
  titleKey: 'desk.error.already_decided_title',
  messageKey: 'desk.error.already_decided_body',
  alreadyDecided: true
}

const message = (messageKey: DeskErrorKey): DeskErrorMessage =>
  ({ titleKey: null, messageKey, alreadyDecided: false })

/** Reads own and inherited properties, so an Error subclass's `name` counts. */
const readString = (source: object, key: string): string => {
  const value = (source as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : ''
}

export const isAlreadyDecided = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false
  return readString(error, 'name') === DESK_ALREADY_DECIDED ||
    readString(error, 'message') === 'desk_not_restorable'
}

export const mapDeskError = (error: unknown): DeskErrorMessage => {
  if (typeof error !== 'object' || error === null) return message('desk.error.save')
  if (isAlreadyDecided(error)) return ALREADY_DECIDED
  if (readString(error, 'message') === 'desk_bad_image') return message('desk.images.change_failed')
  if (readString(error, 'code') === '42501') return message('desk.error.load')
  return message('desk.error.save')
}
